import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Sun, Home, Building2, Factory, MoreHorizontal, Building,
  Zap, Flame, Fuel, TreePine, Waves, HelpCircle, Thermometer,
  CloudSun, ChevronLeft, ChevronRight, CheckCircle2,
  MapPin, User as UserIcon, Mail, Phone, Lock,
  Sparkles, Leaf, Clock, Award, Users, Key, Shield,
  ArrowDownToLine, ArrowUpToLine, Triangle, CalendarDays, Ticket,
} from "lucide-react";

/**
 * Energie-Erweiterung Phase D — Public Embed-Konfigurator.
 *
 * Deklarative Step-Maschine: Produktauswahl (PV / Wärmepumpe / PV+WP) →
 * dynamischer Pfad → gemeinsamer Funnel (Zeitraum, PLZ, Loader, Name,
 * E-Mail, Telefon+Consent, Adresse).
 *
 * Erhalten aus Sprint 9 (unverändert):
 * - Honeypot (_hp_website), Time-Trap (_t_start), Consent-Pflicht
 * - Submit-Endpoint, Branding-CI (--brand-primary/--brand-accent)
 * - Public Embed ohne App-Layout, Datenschutzlink, Powered-by
 */

const API = process.env.REACT_APP_BACKEND_URL || "";

// ============= Antwortoptionen =============

const SOLUTION_OPTIONS = [
  { value: "pv", label: "Solaranlage", hint: "Eigenstrom erzeugen und Energiekosten senken", icon: Sun },
  { value: "heat_pump", label: "Wärmepumpe", hint: "Heizung modernisieren und Förderung nutzen", icon: Thermometer },
  { value: "pv_heat_pump", label: "Solaranlage + Wärmepumpe", hint: "Das Energiesystem intelligent kombinieren", icon: Zap },
];

const BUILDING_PV = [
  { value: "efh", label: "Einfamilienhaus", icon: Home },
  { value: "mfh", label: "Mehrfamilienhaus", icon: Building2 },
  { value: "commercial", label: "Gewerbeobjekt", icon: Factory },
  { value: "other", label: "Anderes", icon: MoreHorizontal },
];

const BUILDING_HP = [
  { value: "efh", label: "Einfamilienhaus", icon: Home },
  { value: "mfh", label: "Mehrfamilienhaus", icon: Building2 },
  { value: "row_house", label: "Reihenhaus", icon: Building },
  { value: "other", label: "Anderes", icon: MoreHorizontal },
];

const OWNERSHIP = [
  { value: "owner", label: "Ja, Eigentümer", icon: Key },
  { value: "tenant", label: "Nein, Mieter", icon: UserIcon },
];

const HOUSEHOLD = [
  { value: "1", label: "1 Person", icon: UserIcon },
  { value: "2", label: "2 Personen", icon: Users },
  { value: "3", label: "3 Personen", icon: Users },
  { value: "4", label: "4 Personen", icon: Users },
  { value: "5_plus", label: "5+ Personen", icon: Users },
];

const HEATING_TYPES = [
  { value: "oil", label: "Öl", icon: Fuel },
  { value: "gas", label: "Gas", icon: Flame },
  { value: "wood_pellet", label: "Holz / Pellet", icon: TreePine },
  { value: "electricity", label: "Strom", icon: Zap },
  { value: "district_heating", label: "Fernwärme", icon: Waves },
  { value: "other_unknown", label: "Anderes / weiß ich nicht", icon: HelpCircle },
];

const HEATED_AREAS = [
  { value: "up_to_100", label: "bis 100 m²" },
  { value: "100_180", label: "100–180 m²" },
  { value: "180_250", label: "180–250 m²" },
  { value: "over_250", label: "über 250 m²" },
  { value: "unknown", label: "weiß ich nicht" },
];

const HEATING_LOCATIONS = [
  { value: "basement", label: "Keller", icon: ArrowDownToLine },
  { value: "ground_floor", label: "Erdgeschoss", icon: Home },
  { value: "upper_floor", label: "Obergeschoss", icon: ArrowUpToLine },
  { value: "attic", label: "Dachgeschoss", icon: Triangle },
  { value: "unknown", label: "weiß ich nicht", icon: HelpCircle },
];

const TIMELINES = [
  { value: "immediate", label: "Sofort", hint: "So schnell wie möglich", icon: Sparkles },
  { value: "under_3_months", label: "In den nächsten 3 Monaten", hint: "Im nächsten Quartal", icon: CalendarDays },
  { value: "over_3_months", label: "Später als 3 Monate", hint: "Mittelfristig geplant", icon: Clock },
  { value: "info_only", label: "Ich möchte mich erstmal informieren", hint: "Ganz unverbindlich", icon: HelpCircle },
];

// ============= Step-Maschine (deklarativ) =============
// Jeder Step: { id, title, autoAdvance? } — Rendering via switch auf id.

const PATH_STEPS = {
  pv: [
    { id: "building_pv", title: "Welche Art von Gebäude möchten Sie ausstatten?" },
    { id: "ownership", title: "Sind Sie Eigentümer der Immobilie?" },
    { id: "household", title: "Wie viele Personen leben ungefähr im Haushalt?" },
  ],
  heat_pump: [
    { id: "heating_type", title: "Wie heizen Sie aktuell?" },
    { id: "heated_area", title: "Wie groß ist die beheizte Wohnfläche ungefähr?" },
    { id: "building_hp", title: "Welche Art von Gebäude ist es?" },
    { id: "heating_location", title: "Wo befindet sich Ihre aktuelle Heizung ungefähr?" },
  ],
  pv_heat_pump: [
    { id: "building_hp", title: "Welche Art von Gebäude möchten Sie modernisieren?" },
    { id: "heating_type", title: "Wie heizen Sie aktuell?" },
    { id: "heating_location", title: "Wo befindet sich Ihre aktuelle Heizung ungefähr?" },
    { id: "household", title: "Wie viele Personen leben ungefähr im Haushalt?" },
  ],
};

const COMMON_FUNNEL = [
  { id: "timeline", title: "Wann möchten Sie Ihr Projekt umsetzen?" },
  { id: "postal", title: "Wo befindet sich die Immobilie?" },
  { id: "loader", title: "Einen Moment bitte …" },
  { id: "name", title: "Wie heißen Sie?" },
  { id: "email", title: "An welche E-Mail-Adresse dürfen wir Ihre Preisschätzung senden?" },
  { id: "phone_consent", title: "Unter welcher Telefonnummer dürfen wir Sie bei Rückfragen erreichen?" },
  { id: "address", title: "Wo soll die Anlage installiert werden?" },
];

const SOLUTION_STEP = { id: "solution", title: "Wofür interessieren Sie sich?" };

function buildSteps(solutionType) {
  return [SOLUTION_STEP, ...(PATH_STEPS[solutionType] || PATH_STEPS.pv), ...COMMON_FUNNEL];
}

export default function EmbedConfigurator() {
  const { id: configuratorId } = useParams();
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState(null);
  const [error, setError] = useState("");
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tStart] = useState(() => Date.now());
  const advanceTimerRef = useRef(null);
  const [form, setForm] = useState({
    solution_type: "",
    building_type: "", ownership_status: "", household_size: "",
    current_heating_type: "", heated_living_area: "", heating_location: "",
    implementation_timeline: "",
    postal_code: "", city: "", street: "", house_number: "",
    first_name: "", last_name: "", email: "", phone: "",
    referral_code: "",
    consent_given: false,
    _hp_website: "",
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/public/configurator/${configuratorId}`);
        if (!alive) return;
        setCfg(r.data);
      } catch (e) {
        if (!alive) return;
        setError(e?.response?.status === 410 ? "inactive" : "load_error");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [configuratorId]);

  useEffect(() => () => { if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current); }, []);

  const steps = useMemo(() => buildSteps(form.solution_type || "pv"), [form.solution_type]);
  const current = steps[Math.min(step, steps.length - 1)];
  const TOTAL = steps.length;

  // Loader: automatisch weiter nach kurzer Vorbereitung
  useEffect(() => {
    if (current?.id !== "loader" || submitted) return undefined;
    const t = setTimeout(() => setStep((s) => Math.min(s + 1, TOTAL - 1)), 2200);
    return () => clearTimeout(t);
  }, [current?.id, submitted, TOTAL]);

  const company = cfg?.company || {};
  const styleVars = useMemo(() => ({
    "--brand-primary": company.primary_color || "#f97316",
    "--brand-accent": company.accent_color || "#0f172a",
  }), [company.primary_color, company.accent_color]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  // Produktauswahl: bei Wechsel die Pfad-Antworten zurücksetzen (gemeinsamer Funnel bleibt)
  const pickSolution = (value) => {
    setForm((s) => (s.solution_type === value ? s : {
      ...s, solution_type: value,
      building_type: "", ownership_status: "", household_size: "",
      current_heating_type: "", heated_living_area: "", heating_location: "",
    }));
    if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
    advanceTimerRef.current = setTimeout(() => setStep(1), 280);
  };

  const canNext = () => {
    switch (current?.id) {
      case "solution": return !!form.solution_type;
      case "building_pv":
      case "building_hp": return !!form.building_type;
      case "ownership": return !!form.ownership_status;
      case "household": return !!form.household_size;
      case "heating_type": return !!form.current_heating_type;
      case "heated_area": return !!form.heated_living_area;
      case "heating_location": return !!form.heating_location;
      case "timeline": return !!form.implementation_timeline;
      case "postal": return form.postal_code.length >= 4;
      case "loader": return true;
      case "name": return form.first_name.trim().length > 0;
      case "email": return form.email.includes("@");
      case "phone_consent": return form.consent_given;
      case "address": return form.postal_code.length >= 4; // PLZ bleibt Pflicht, Adresse optional (nicht verschärft)
      default: return true;
    }
  };

  const goNext = () => { if (step < TOTAL - 1) setStep(step + 1); };
  const goBack = () => {
    if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
    if (step > 0) setStep(step - 1);
  };

  // Single-Choice: Auswahl kurz zeigen, dann weiter
  const pickAndAdvance = (key, value, { noAdvance = false } = {}) => {
    set(key, value);
    if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
    if (!noAdvance) advanceTimerRef.current = setTimeout(() => goNext(), 280);
  };

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const installation_address = [form.street, form.house_number].filter(Boolean).join(" ").trim();
      const payload = {
        // Kanonische neue Felder (Backend-Whitelist Phase C)
        solution_type: form.solution_type || "pv",
        implementation_timeline: form.implementation_timeline,
        building_type: form.building_type,
        ownership_status: form.ownership_status,
        household_size: form.household_size,
        current_heating_type: form.current_heating_type,
        heated_living_area: form.heated_living_area,
        heating_location: form.heating_location,
        postal_code: form.postal_code,
        installation_address,
        city: form.city,
        // Kontakt
        first_name: form.first_name, last_name: form.last_name,
        email: form.email, phone: form.phone,
        notes: form.referral_code ? `Empfehlungscode: ${form.referral_code}` : "",
        // Consent + Schutzmechanismen (unverändert)
        consent_given: form.consent_given,
        _hp_website: form._hp_website,
        _t_start: tStart,
        source_url: typeof window !== "undefined" ? document.referrer : "",
      };
      const r = await axios.post(`${API}/api/public/configurator/${configuratorId}/submit`, payload);
      setSubmitted(true);
      if (r.data?.redirect_url) {
        setTimeout(() => { window.location.href = r.data.redirect_url; }, 2500);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 font-sans">
        <div className="flex flex-col items-center gap-3 text-slate-500">
          <div className="w-10 h-10 rounded-full border-2 border-slate-200 border-t-orange-500 animate-spin" />
          <p className="text-sm">Wird geladen …</p>
        </div>
      </div>
    );
  }
  if (error === "inactive" || !cfg) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 font-sans">
        <div className="text-center max-w-sm px-6">
          <div className="w-14 h-14 rounded-full bg-slate-100 grid place-items-center mx-auto mb-3">
            <CloudSun className="w-7 h-7 text-slate-400" />
          </div>
          <p className="text-slate-700 font-semibold mb-1">Konfigurator nicht verfügbar</p>
          <p className="text-sm text-slate-500">Dieser Konfigurator ist derzeit deaktiviert. Bitte versuchen Sie es später erneut.</p>
        </div>
      </div>
    );
  }

  const privacyHref = company.privacy_url || "https://revenioo.ai/datenschutz";
  const imprintHref = company.imprint_url || "";
  const enabledSolutions = cfg.enabled_solution_types || ["pv", "heat_pump", "pv_heat_pump"];
  const progressPct = Math.round(((step + (submitted ? 1 : 0)) / TOTAL) * 100);
  const isLastStep = step === TOTAL - 1;
  const isLoader = current?.id === "loader";

  return (
    <div style={styleVars} className="min-h-screen font-sans text-slate-900" data-testid="embed-configurator-root">
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 pointer-events-none"
        style={{
          background: `radial-gradient(900px 500px at 20% -10%, color-mix(in oklab, var(--brand-primary) 15%, transparent), transparent 60%),
                       radial-gradient(800px 500px at 100% 100%, color-mix(in oklab, var(--brand-accent) 10%, transparent), transparent 60%),
                       linear-gradient(180deg, #fafaf9 0%, #f1f5f9 100%)`,
        }}
      />

      <div className="relative max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <header className="flex items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-3 min-w-0">
            {company.logo_url ? (
              <img src={company.logo_url} alt={company.name || ""} className="w-11 h-11 object-contain rounded-lg bg-white border border-slate-200 p-1" />
            ) : (
              <div
                className="w-11 h-11 rounded-xl grid place-items-center shadow-sm"
                style={{ background: "linear-gradient(135deg, var(--brand-primary), color-mix(in oklab, var(--brand-primary) 60%, #000))" }}
              >
                <Sun className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold truncate">
                {company.name || "Energie-Konfigurator"}
              </p>
              <h1 className="text-base sm:text-lg font-extrabold tracking-tight text-slate-900 truncate" data-testid="embed-title">
                {cfg.title}
              </h1>
            </div>
          </div>
          <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/80 backdrop-blur border border-slate-200 text-[11px] font-semibold text-slate-600">
            <Lock className="w-3 h-3" /> SSL
          </span>
        </header>

        {!submitted ? (
          <>
            {step === 0 && (
              <div className="mb-5">
                <p className="text-slate-600 text-sm sm:text-base">{cfg.subtitle}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <TrustChip icon={Clock} label="In 90 Sek." />
                  <TrustChip icon={Shield} label="DSGVO" />
                  <TrustChip icon={Award} label="Unverbindlich" />
                  <TrustChip icon={Leaf} label="0 €" />
                </div>
              </div>
            )}

            {/* Progress */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2 text-[11px] uppercase tracking-widest font-bold">
                <span className="text-slate-500">
                  Schritt <span className="text-slate-900">{step + 1}</span> von {TOTAL}
                </span>
                <span style={{ color: "var(--brand-primary)" }}>{progressPct}%</span>
              </div>
              <div className="h-2 rounded-full bg-slate-200/70 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500 ease-out"
                  style={{
                    width: `${(step / (TOTAL - 1)) * 100}%`,
                    background: "linear-gradient(90deg, var(--brand-primary), color-mix(in oklab, var(--brand-primary) 60%, #000))",
                  }}
                />
              </div>
            </div>

            {/* Question card */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 sm:p-8 min-h-[360px] flex flex-col">
              {/* HONEYPOT (versteckt, unverändert) */}
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form._hp_website}
                onChange={(e) => set("_hp_website", e.target.value)}
                style={{ position: "absolute", left: "-10000px", width: 1, height: 1, opacity: 0 }}
                aria-hidden="true"
                data-testid="honeypot-field"
              />

              <div key={`${current.id}-${step}`} className="animate-fade-in flex-1">
                {!isLoader && <QuestionHeader title={current.title} stepIndex={step} />}

                {current.id === "solution" && (
                  <StepSolution form={form} pick={pickSolution} enabled={enabledSolutions} />
                )}
                {current.id === "building_pv" && (
                  <CardGrid testidPrefix="building" options={BUILDING_PV} value={form.building_type} onPick={(v) => pickAndAdvance("building_type", v)} />
                )}
                {current.id === "building_hp" && (
                  <CardGrid testidPrefix="building" options={BUILDING_HP} value={form.building_type} onPick={(v) => pickAndAdvance("building_type", v)} />
                )}
                {current.id === "ownership" && <StepOwnership form={form} pick={pickAndAdvance} />}
                {current.id === "household" && (
                  <CardGrid testidPrefix="household" options={HOUSEHOLD} value={form.household_size} onPick={(v) => pickAndAdvance("household_size", v)} cols3 />
                )}
                {current.id === "heating_type" && (
                  <CardGrid testidPrefix="heating-type" options={HEATING_TYPES} value={form.current_heating_type} onPick={(v) => pickAndAdvance("current_heating_type", v)} />
                )}
                {current.id === "heated_area" && (
                  <CardGrid testidPrefix="heated-area" options={HEATED_AREAS.map((o) => ({ ...o, icon: RulerIcon }))} value={form.heated_living_area} onPick={(v) => pickAndAdvance("heated_living_area", v)} />
                )}
                {current.id === "heating_location" && (
                  <CardGrid testidPrefix="heating-location" options={HEATING_LOCATIONS} value={form.heating_location} onPick={(v) => pickAndAdvance("heating_location", v)} />
                )}
                {current.id === "timeline" && <StepTimeline form={form} pick={pickAndAdvance} />}
                {current.id === "postal" && <StepPostal form={form} set={set} onEnter={canNext() ? goNext : undefined} />}
                {current.id === "loader" && <StepLoader />}
                {current.id === "name" && <StepName form={form} set={set} onEnter={canNext() ? goNext : undefined} />}
                {current.id === "email" && <StepEmail form={form} set={set} onEnter={canNext() ? goNext : undefined} />}
                {current.id === "phone_consent" && (
                  <StepPhoneConsent form={form} set={set} privacyHref={privacyHref} imprintHref={imprintHref} companyName={company.name || cfg.title} />
                )}
                {current.id === "address" && <StepAddress form={form} set={set} />}
              </div>

              {error && error !== "inactive" && error !== "load_error" && (
                <div className="mt-4 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-sm text-red-800" data-testid="embed-error">
                  {error}
                </div>
              )}

              {/* Navigation */}
              {!isLoader && (
                <div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-between gap-3">
                  <button
                    type="button"
                    disabled={step === 0}
                    onClick={goBack}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-500 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    data-testid="embed-back-btn"
                  >
                    <ChevronLeft className="w-4 h-4" /> Zurück
                  </button>
                  {!isLastStep ? (
                    <button
                      type="button"
                      disabled={!canNext()}
                      onClick={goNext}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold text-white shadow-md hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 hover:-translate-y-px"
                      style={{ background: "var(--brand-primary)" }}
                      data-testid="embed-next-btn"
                    >
                      Weiter <ChevronRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={!canNext() || submitting}
                      onClick={submit}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold text-white shadow-md hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 hover:-translate-y-px"
                      style={{ background: "var(--brand-primary)" }}
                      data-testid="embed-submit-btn"
                    >
                      {submitting ? (
                        <>
                          <span className="w-4 h-4 rounded-full border-2 border-white/60 border-t-white animate-spin" />
                          Wird gesendet …
                        </>
                      ) : (
                        <>
                          Preisschätzung anfordern <ChevronRight className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="mt-4 flex items-center justify-center gap-4 text-[11px] text-slate-500">
              <span className="inline-flex items-center gap-1.5"><Lock className="w-3 h-3" /> Verschlüsselt</span>
              <span>·</span>
              <span className="inline-flex items-center gap-1.5"><Shield className="w-3 h-3" /> DSGVO</span>
              <span>·</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3" /> 0 € unverbindlich</span>
            </div>
          </>
        ) : (
          <ThankYou cfg={cfg} company={company} form={form} />
        )}

        <footer className="text-center text-[10px] text-slate-400 mt-8" data-testid="embed-footer">
          Powered by{" "}
          <a href="https://revenioo.ai" target="_blank" rel="noreferrer" className="font-bold hover:underline">
            revenioo.ai
          </a>
        </footer>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fadeIn 0.35s ease-out both; }
      `}</style>
    </div>
  );
}

// =================== Header ===================
function QuestionHeader({ title, stepIndex }) {
  return (
    <div className="mb-5">
      <p className="text-[11px] uppercase tracking-[0.18em] font-bold mb-2" style={{ color: "var(--brand-primary)" }}>
        Schritt {stepIndex + 1}
      </p>
      <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 leading-tight">
        {title}
      </h2>
    </div>
  );
}

// =================== Schritt 1: Produktauswahl ===================
function StepSolution({ form, pick, enabled }) {
  const options = SOLUTION_OPTIONS.filter((o) => enabled.includes(o.value));
  return (
    <div data-testid="step-solution">
      <p className="text-sm text-slate-500 mb-4">Wählen Sie Ihr Projekt — wir stellen nur die Fragen, die dazu passen.</p>
      <div className="grid grid-cols-1 gap-3">
        {options.map((o) => (
          <BigChoiceCard
            key={o.value}
            icon={o.icon}
            label={o.label}
            hint={o.hint}
            selected={form.solution_type === o.value}
            onClick={() => pick(o.value)}
            testid={`solution-card-${o.value}`}
          />
        ))}
      </div>
    </div>
  );
}

// =================== PV: Eigentümer (mit Mieter-Hinweis, kein Abbruch) ===================
function StepOwnership({ form, pick }) {
  return (
    <div data-testid="step-ownership">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {OWNERSHIP.map((o) => (
          <BigChoiceCard
            key={o.value}
            icon={o.icon}
            label={o.label}
            selected={form.ownership_status === o.value}
            onClick={() => pick("ownership_status", o.value, { noAdvance: o.value === "tenant" })}
            testid={`ownership-card-${o.value}`}
          />
        ))}
      </div>
      {form.ownership_status === "tenant" && (
        <div
          className="mt-4 p-3.5 rounded-xl border text-sm flex items-start gap-2.5"
          style={{
            background: "color-mix(in oklab, var(--brand-primary) 6%, white)",
            borderColor: "color-mix(in oklab, var(--brand-primary) 25%, white)",
          }}
          data-testid="tenant-hint"
        >
          <Sparkles className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--brand-primary)" }} />
          <p className="text-slate-700">
            Für eine Solaranlage benötigen wir in der Regel die Zustimmung des Eigentümers.
            Wir können Ihnen trotzdem eine erste Einschätzung geben.
          </p>
        </div>
      )}
    </div>
  );
}

// =================== Gemeinsamer Funnel ===================
function StepTimeline({ form, pick }) {
  return (
    <div data-testid="step-timeline">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {TIMELINES.map((t) => (
          <BigChoiceCard
            key={t.value}
            icon={t.icon}
            label={t.label}
            hint={t.hint}
            selected={form.implementation_timeline === t.value}
            onClick={() => pick("implementation_timeline", t.value, { noAdvance: t.value === "immediate" })}
            testid={`timeline-card-${t.value}`}
          />
        ))}
      </div>
      {form.implementation_timeline === "immediate" && (
        <div
          className="mt-4 p-3.5 rounded-xl border-2 text-sm flex items-center gap-2.5 font-semibold"
          style={{
            background: "color-mix(in oklab, var(--brand-primary) 10%, white)",
            borderColor: "var(--brand-primary)",
            color: "var(--brand-accent)",
          }}
          data-testid="immediate-priority-hint"
        >
          <Zap className="w-4 h-4 shrink-0" style={{ color: "var(--brand-primary)" }} />
          Wir priorisieren Ihre Anfrage.
        </div>
      )}
    </div>
  );
}

function StepPostal({ form, set, onEnter }) {
  return (
    <div data-testid="step-postal">
      <F label="Postleitzahl *" testid="field-zip">
        <Input
          value={form.postal_code}
          onChange={(e) => set("postal_code", e.target.value.replace(/\D/g, "").slice(0, 5))}
          placeholder="86163"
          inputMode="numeric"
          autoFocus
          data-testid="input-zip"
          iconLeft={MapPin}
          onKeyDown={(e) => e.key === "Enter" && onEnter?.()}
        />
      </F>
      <HintBox>Wir prüfen, ob Ihr Standort im Servicegebiet liegt.</HintBox>
    </div>
  );
}

function StepLoader() {
  return (
    <div className="flex flex-col items-center justify-center text-center py-14" data-testid="step-loader">
      <div className="relative w-20 h-20 mb-6">
        <div className="absolute inset-0 rounded-full border-4 border-slate-100" />
        <div
          className="absolute inset-0 rounded-full border-4 border-transparent animate-spin"
          style={{ borderTopColor: "var(--brand-primary)" }}
        />
        <div className="absolute inset-0 grid place-items-center">
          <Sparkles className="w-7 h-7" style={{ color: "var(--brand-primary)" }} />
        </div>
      </div>
      <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight mb-2">
        Wir bereiten Ihre persönliche Preisschätzung vor …
      </h2>
      <p className="text-sm text-slate-500 max-w-sm">
        Gleich noch Ihre Kontaktdaten — dann senden wir Ihnen die unverbindliche Einschätzung zu.
      </p>
    </div>
  );
}

function StepName({ form, set, onEnter }) {
  return (
    <div data-testid="step-name">
      <p className="text-sm text-slate-500 mb-4">Damit wir Sie persönlich ansprechen können.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <F label="Vorname *">
          <Input
            value={form.first_name}
            onChange={(e) => set("first_name", e.target.value)}
            placeholder="Anna"
            data-testid="input-first-name"
            iconLeft={UserIcon}
            autoFocus
            onKeyDown={(e) => e.key === "Enter" && onEnter?.()}
          />
        </F>
        <F label="Nachname">
          <Input
            value={form.last_name}
            onChange={(e) => set("last_name", e.target.value)}
            placeholder="Müller"
            data-testid="input-last-name"
            onKeyDown={(e) => e.key === "Enter" && onEnter?.()}
          />
        </F>
      </div>
    </div>
  );
}

function StepEmail({ form, set, onEnter }) {
  return (
    <div data-testid="step-email">
      <p className="text-sm text-slate-500 mb-4">Dorthin senden wir Ihre unverbindliche Preisschätzung.</p>
      <F label="E-Mail *">
        <Input
          type="email"
          value={form.email}
          onChange={(e) => set("email", e.target.value)}
          placeholder="anna@example.de"
          data-testid="input-email"
          iconLeft={Mail}
          autoFocus
          onKeyDown={(e) => e.key === "Enter" && onEnter?.()}
        />
      </F>
    </div>
  );
}

function StepPhoneConsent({ form, set, privacyHref, imprintHref, companyName }) {
  return (
    <div data-testid="step-phone-consent">
      <F label="Telefon (optional)">
        <Input
          type="tel"
          value={form.phone}
          onChange={(e) => set("phone", e.target.value)}
          placeholder="+49 170 1234567"
          data-testid="input-phone"
          iconLeft={Phone}
          autoFocus
        />
      </F>
      <F label="Empfehlungscode (optional)">
        <Input
          value={form.referral_code}
          onChange={(e) => set("referral_code", e.target.value.slice(0, 40))}
          placeholder="z.B. FREUNDE2026"
          data-testid="input-referral-code"
          iconLeft={Ticket}
        />
      </F>

      <div
        className="mt-2 p-4 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/60 flex items-start gap-3"
        data-testid="consent-row"
      >
        <input
          type="checkbox"
          className="mt-1 w-5 h-5 rounded border-slate-300 cursor-pointer"
          style={{ accentColor: "var(--brand-primary)" }}
          checked={form.consent_given}
          onChange={(e) => set("consent_given", e.target.checked)}
          data-testid="consent-checkbox"
          id="cfg-consent"
        />
        <label htmlFor="cfg-consent" className="text-xs sm:text-sm text-slate-700 leading-relaxed cursor-pointer">
          Ich stimme zu, dass meine Angaben zur Bearbeitung meiner Anfrage durch{" "}
          <strong className="text-slate-900">{companyName}</strong> verarbeitet werden. Details in der{" "}
          <a
            href={privacyHref}
            target="_blank"
            rel="noreferrer"
            className="underline font-semibold"
            style={{ color: "var(--brand-primary)" }}
            data-testid="consent-privacy-link"
          >
            Datenschutzerklärung
          </a>
          .{" "}
          {imprintHref && (
            <>· <a href={imprintHref} target="_blank" rel="noreferrer" className="underline">Impressum</a></>
          )}
          <span className="text-red-600 ml-0.5">*</span>
        </label>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <TrustChip icon={Lock} label="SSL-verschlüsselt" />
        <TrustChip icon={Shield} label="DSGVO-konform" />
        <TrustChip icon={Award} label="Unverbindlich" />
      </div>
    </div>
  );
}

function StepAddress({ form, set }) {
  return (
    <div data-testid="step-address">
      <p className="text-sm text-slate-500 mb-4">Für eine genauere Einschätzung — die Adresse ist optional, die PLZ bleibt erforderlich.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <F label="Straße (optional)" className="sm:col-span-2">
          <Input
            value={form.street}
            onChange={(e) => set("street", e.target.value)}
            placeholder="Solarweg"
            data-testid="input-street"
            autoFocus
          />
        </F>
        <F label="Hausnummer (optional)">
          <Input
            value={form.house_number}
            onChange={(e) => set("house_number", e.target.value.slice(0, 10))}
            placeholder="12"
            data-testid="input-house-number"
          />
        </F>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <F label="PLZ *">
          <Input
            value={form.postal_code}
            onChange={(e) => set("postal_code", e.target.value.replace(/\D/g, "").slice(0, 5))}
            inputMode="numeric"
            placeholder="86163"
            data-testid="input-address-zip"
            iconLeft={MapPin}
          />
        </F>
        <F label="Ort (optional)" className="sm:col-span-2">
          <Input
            value={form.city}
            onChange={(e) => set("city", e.target.value)}
            placeholder="Augsburg"
            data-testid="input-city"
          />
        </F>
      </div>
    </div>
  );
}

// =================== ThankYou ===================
function ThankYou({ cfg, company, form }) {
  return (
    <div
      className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 sm:p-12 text-center relative overflow-hidden"
      data-testid="embed-thanks"
    >
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-32 -z-0 opacity-40"
        style={{ background: `radial-gradient(500px 240px at 50% 0%, var(--brand-primary), transparent 70%)` }}
      />
      <div className="relative">
        <div
          className="w-20 h-20 rounded-full grid place-items-center mx-auto mb-5 shadow-lg"
          style={{ background: "linear-gradient(135deg, var(--brand-primary), color-mix(in oklab, var(--brand-primary) 60%, #000))" }}
        >
          <CheckCircle2 className="w-10 h-10 text-white" strokeWidth={2.5} />
        </div>
        <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-3">Vielen Dank!</h2>
        <p className="text-sm sm:text-base text-slate-700 max-w-md mx-auto font-medium" data-testid="thanks-estimate-note">
          Ihre persönliche Preisschätzung wird vorbereitet und Ihnen per E-Mail zugesendet.
        </p>
        {form?.implementation_timeline === "immediate" && (
          <p
            className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold"
            style={{
              background: "color-mix(in oklab, var(--brand-primary) 12%, white)",
              color: "var(--brand-accent)",
            }}
            data-testid="thanks-priority-note"
          >
            <Zap className="w-4 h-4" style={{ color: "var(--brand-primary)" }} />
            Da Sie eine sofortige Umsetzung wünschen, wird Ihre Anfrage priorisiert.
          </p>
        )}
        {cfg.thank_you_text && (
          <p className="mt-4 text-xs sm:text-sm text-slate-500 whitespace-pre-line max-w-md mx-auto">
            {cfg.thank_you_text}
          </p>
        )}
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <TrustChip icon={Mail} label="Preisschätzung per E-Mail" />
          <TrustChip icon={Award} label="Unverbindlich" />
          <TrustChip icon={Shield} label="DSGVO" />
        </div>
        {company.support_email && (
          <p className="mt-6 text-xs text-slate-500">
            Bei Fragen: <a href={`mailto:${company.support_email}`} className="underline">{company.support_email}</a>
          </p>
        )}
      </div>
    </div>
  );
}

// =================== UI Helpers ===================
const F = ({ label, children, testid, className = "" }) => (
  <label className={`block text-sm mb-3 ${className}`} data-testid={testid}>
    <span className="block text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">{label}</span>
    {children}
  </label>
);

const Input = ({ iconLeft: IconLeft, className = "", style, ...props }) => (
  <div className="relative">
    {IconLeft && (
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
        <IconLeft className="w-4 h-4" />
      </span>
    )}
    <input
      {...props}
      className={`w-full border border-slate-200 rounded-lg ${IconLeft ? "pl-9" : "px-3"} pr-3 py-3 text-base sm:text-sm bg-white focus:outline-none focus:ring-2 focus:border-transparent transition-shadow ${className}`}
      style={{ "--tw-ring-color": "var(--brand-primary)", ...(style || {}) }}
    />
  </div>
);

const BigChoiceCard = ({ icon: Icon, label, hint, selected, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`group relative text-left p-4 rounded-xl border-2 transition-all duration-150 hover:shadow-md hover:-translate-y-px ${
      selected ? "shadow-lg" : "border-slate-200 bg-white hover:border-slate-300"
    }`}
    style={
      selected
        ? { background: "color-mix(in oklab, var(--brand-primary) 10%, white)", borderColor: "var(--brand-primary)" }
        : {}
    }
  >
    <div className="flex items-center gap-3">
      <span
        className={`w-12 h-12 rounded-xl grid place-items-center shrink-0 transition-colors ${
          selected ? "text-white" : "bg-slate-100 text-slate-400"
        }`}
        style={selected ? { background: "var(--brand-primary)" } : {}}
      >
        <Icon className="w-5 h-5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-bold ${selected ? "text-slate-900" : "text-slate-700"}`}>{label}</p>
        {hint && <p className="text-[11px] text-slate-500 mt-0.5">{hint}</p>}
      </div>
      {selected && (
        <span className="w-6 h-6 rounded-full grid place-items-center text-white shrink-0" style={{ background: "var(--brand-primary)" }}>
          <CheckCircle2 className="w-4 h-4" />
        </span>
      )}
    </div>
  </button>
);

/** Generisches Antwortkarten-Grid für Single-Choice-Fragen */
const CardGrid = ({ options, value, onPick, testidPrefix, cols3 = false }) => (
  <div data-testid={`grid-${testidPrefix}`} className={`grid grid-cols-1 ${cols3 ? "sm:grid-cols-3" : "sm:grid-cols-2"} gap-3`}>
    {options.map((o) => (
      <BigChoiceCard
        key={o.value}
        icon={o.icon || MoreHorizontal}
        label={o.label}
        hint={o.hint}
        selected={value === o.value}
        onClick={() => onPick(o.value)}
        testid={`${testidPrefix}-card-${o.value}`}
      />
    ))}
  </div>
);

const TrustChip = ({ icon: Icon, label }) => (
  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-slate-200 text-[11px] font-semibold text-slate-600 shadow-xs">
    <Icon className="w-3 h-3" style={{ color: "var(--brand-primary)" }} />
    {label}
  </span>
);

const HintBox = ({ children }) => (
  <div
    className="mt-4 p-3 rounded-lg border text-xs flex items-start gap-2"
    style={{
      background: "color-mix(in oklab, var(--brand-primary) 6%, white)",
      borderColor: "color-mix(in oklab, var(--brand-primary) 25%, white)",
      color: "var(--brand-accent)",
    }}
  >
    <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "var(--brand-primary)" }} />
    <p>{children}</p>
  </div>
);

// Flächen-Icon (einfaches SVG, keine externen Abhängigkeiten)
const RulerIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <rect x="3" y="9" width="18" height="6" rx="1" />
    <path d="M7 9v2M11 9v3M15 9v2M19 9v3" />
  </svg>
);
